// File: /opt/dev_environment/assets/workspace/app-init/src/pages/VerbenPage/StagedVerbsSection.js

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVocabulary } from '../../context/VocabularyContext';
import { stagedVerbApi } from '../../services/stagedVerbApi';
import {
  VERB_STATUS_CONFIG,
  TENSE_KEYS,
  TENSE_CONFIG,
  PERSON_CONFIG,
  VERB_GROUP_LABELS,
  calculateImportCardCount,
} from '../../constants/stagedVerbConstants';
import './StagedVerbsSection.css';

// ============================================
// SUB-COMPONENTS
// ============================================

function StatusBadge({ status }) {
  const config = VERB_STATUS_CONFIG[status] || VERB_STATUS_CONFIG.pending;
  return (
    <span
      className="staged-verbs__status-badge"
      style={{ color: config.color, backgroundColor: config.bgColor }}
    >
      {config.icon} {config.label}
    </span>
  );
}

function VerbDetailDialog({ verb, onClose, onImport, onDelete, onUpdate }) {
  const [selectedForms, setSelectedForms] = useState(
    verb.selected_forms || TENSE_KEYS.filter((k) => verb.conjugations && verb.conjugations[k])
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const handleTenseToggle = useCallback(async (tenseKey) => {
    const newForms = selectedForms.includes(tenseKey)
      ? selectedForms.filter((f) => f !== tenseKey)
      : [...selectedForms, tenseKey];
    setSelectedForms(newForms);

    setIsSaving(true);
    try {
      await stagedVerbApi.updateVerb(verb.id, { selected_forms: newForms });
      if (onUpdate) onUpdate({ ...verb, selected_forms: newForms });
    } catch (err) {
      console.error('Failed to update selected forms:', err);
    } finally {
      setIsSaving(false);
    }
  }, [selectedForms, verb, onUpdate]);

  const handleImportClick = useCallback(() => {
    onImport([verb], selectedForms);
  }, [verb, selectedForms, onImport]);

  const structuredConjugations = verb.structured_conjugations || [];
  const rawConjugations = verb.conjugations || {};

  const tenseDisplayData = useMemo(() => {
    if (structuredConjugations.length > 0) {
      return structuredConjugations;
    }
    return TENSE_KEYS
      .filter((key) => rawConjugations[key])
      .map((key) => ({
        tense_key: key,
        tense_label: TENSE_CONFIG[key]?.label || key,
        tense_german_label: TENSE_CONFIG[key]?.germanLabel || '',
        conjugations: Object.entries(rawConjugations[key]).map(([person, italian]) => ({
          person,
          person_label: PERSON_CONFIG[person]?.label || person,
          person_german_label: PERSON_CONFIG[person]?.germanLabel || person,
          italian,
          german: null,
        })),
      }));
  }, [structuredConjugations, rawConjugations]);

  const isReady = verb.status === 'ready';
  const canImport = isReady && selectedForms.length > 0;

  return (
    <div
      className="verb-detail-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={`Verb-Details: ${verb.italian_infinitive}`}
    >
      <div className="verb-detail-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="verb-detail-dialog__header">
          <div className="verb-detail-dialog__header-info">
            <h2 className="verb-detail-dialog__title">
              {verb.italian_infinitive}
            </h2>
            <span className="verb-detail-dialog__subtitle">
              {verb.german_infinitive || '—'}
            </span>
            <div className="verb-detail-dialog__meta">
              {verb.verb_group && (
                <span className="staged-verbs__group-badge">
                  {VERB_GROUP_LABELS[verb.verb_group] || verb.verb_group}
                </span>
              )}
              {verb.auxiliary_verb && (
                <span className="staged-verbs__group-badge">
                  aux: {verb.auxiliary_verb}
                </span>
              )}
              {verb.is_reflexive && (
                <span className="staged-verbs__group-badge">reflexiv</span>
              )}
              <StatusBadge status={verb.status} />
            </div>
          </div>
          <button
            className="verb-detail-dialog__close"
            type="button"
            onClick={onClose}
            aria-label="Dialog schließen"
          >
            ✕
          </button>
        </div>

        <div className="verb-detail-dialog__body">
          {isReady && (
            <div className="verb-detail__tense-section">
              <div className="verb-detail__tense-section-label">
                Zeitformen zum Importieren auswählen
              </div>
              <div className="verb-detail__tense-list">
                {TENSE_KEYS.map((key) => {
                  const config = TENSE_CONFIG[key];
                  const hasData = rawConjugations[key] || tenseDisplayData.some((t) => t.tense_key === key);
                  if (!hasData) return null;
                  return (
                    <label key={key} className="verb-detail__tense-item">
                      <input
                        type="checkbox"
                        className="verb-detail__tense-checkbox"
                        checked={selectedForms.includes(key)}
                        onChange={() => handleTenseToggle(key)}
                        disabled={isSaving}
                      />
                      <span className="verb-detail__tense-label">
                        {config?.label || key}
                      </span>
                      <span className="verb-detail__tense-german">
                        ({config?.germanLabel || ''})
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="verb-detail__conjugation-section">
            {tenseDisplayData.map((tense) => (
              <div key={tense.tense_key} className="verb-detail__conjugation-table-wrapper">
                <div className="verb-detail__conjugation-table-header">
                  <span className="verb-detail__conjugation-table-title">
                    {tense.tense_label}
                  </span>
                  <span className="verb-detail__conjugation-table-german">
                    {tense.tense_german_label}
                  </span>
                </div>
                <table className="verb-detail__conjugation-table">
                  <tbody>
                    {tense.conjugations.map((conj) => (
                      <tr key={conj.person}>
                        <td className="verb-detail__conj-person">
                          {conj.person_label}
                        </td>
                        <td className="verb-detail__conj-italian">
                          {conj.italian}
                        </td>
                        <td className="verb-detail__conj-german">
                          {conj.german || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {verb.status === 'error' && verb.error_message && (
            <div className="staged-verbs__error">
              ❌ {verb.error_message}
            </div>
          )}
        </div>

        <div className="verb-detail-dialog__footer">
          <button
            className="verb-detail__action-btn verb-detail__action-btn--delete"
            type="button"
            onClick={() => onDelete(verb)}
          >
            🗑️ Löschen
          </button>
          {isReady && (
            <button
              className="verb-detail__action-btn verb-detail__action-btn--import"
              type="button"
              onClick={handleImportClick}
              disabled={!canImport}
            >
              📥 Importieren ({calculateImportCardCount(1, selectedForms)} Karten)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportDialog({ verbs, onClose, onConfirm, isImporting }) {
  const [selectedForms, setSelectedForms] = useState(() => {
    const allForms = new Set();
    verbs.forEach((v) => {
      (v.selected_forms || TENSE_KEYS).forEach((f) => allForms.add(f));
    });
    return Array.from(allForms);
  });
  const [addToVocabulary, setAddToVocabulary] = useState(true);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isImporting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isImporting]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && !isImporting) onClose();
  }, [onClose, isImporting]);

  const handleFormToggle = useCallback((formKey) => {
    setSelectedForms((prev) =>
      prev.includes(formKey)
        ? prev.filter((f) => f !== formKey)
        : [...prev, formKey]
    );
  }, []);

  const totalCards = calculateImportCardCount(verbs.length, selectedForms);

  const handleConfirm = useCallback(() => {
    onConfirm(verbs, selectedForms, addToVocabulary);
  }, [verbs, selectedForms, addToVocabulary, onConfirm]);

  return (
    <div
      className="verb-detail-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Verben importieren"
    >
      <div className="import-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="import-dialog__icon" role="img" aria-hidden="true">📥</div>
        <h3 className="import-dialog__title">
          {verbs.length} {verbs.length === 1 ? 'Verb' : 'Verben'} importieren?
        </h3>

        <div className="import-dialog__summary">
          {verbs.map((v) => (
            <div key={v.id} className="import-dialog__summary-item">
              <span>•</span>
              <strong>{v.italian_infinitive}</strong>
              <span>({v.german_infinitive || '—'})</span>
            </div>
          ))}
          <div className="import-dialog__total">
            {totalCards} Verbkarten werden erstellt
            {addToVocabulary && ` + ${verbs.length} Vokabelkarten`}
          </div>
        </div>

        <div className="import-dialog__tense-override">
          <div className="import-dialog__tense-override-label">
            Zeitformen auswählen
          </div>
          <div className="import-dialog__tense-chips">
            {TENSE_KEYS.map((key) => {
              const config = TENSE_CONFIG[key];
              return (
                <button
                  key={key}
                  className={`import-dialog__tense-chip ${selectedForms.includes(key) ? 'import-dialog__tense-chip--selected' : ''}`}
                  type="button"
                  onClick={() => handleFormToggle(key)}
                  disabled={isImporting}
                >
                  {config?.label || key}
                </button>
              );
            })}
          </div>
        </div>

        <label className="import-dialog__vocab-toggle">
          <input
            type="checkbox"
            className="import-dialog__vocab-checkbox"
            checked={addToVocabulary}
            onChange={(e) => setAddToVocabulary(e.target.checked)}
            disabled={isImporting}
          />
          <div>
            <div className="import-dialog__vocab-label">
              Auch als Vokabelkarten hinzufügen
            </div>
            <div className="import-dialog__vocab-hint">
              Erstellt Vokabelkarten für die Infinitivformen
            </div>
          </div>
        </label>

        <div className="import-dialog__actions">
          <button
            className="import-dialog__btn import-dialog__btn--cancel"
            type="button"
            onClick={onClose}
            disabled={isImporting}
          >
            Abbrechen
          </button>
          <button
            className="import-dialog__btn import-dialog__btn--import"
            type="button"
            onClick={handleConfirm}
            disabled={isImporting || selectedForms.length === 0}
          >
            {isImporting ? (
              <>
                <span className="staged-verbs__processing-spinner" />
                Importieren…
              </>
            ) : (
              `Importieren (${totalCards})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportResults({ results, onGoToTraining, onAddMore }) {
  return (
    <div className="import-results">
      <span className="import-results__icon" role="img" aria-hidden="true">✅</span>
      <h3 className="import-results__title">Import abgeschlossen!</h3>

      <div className="import-results__stats">
        <div className="import-results__stat">
          <span>•</span>
          <strong>{results.imported_verbs}</strong> Verben importiert
        </div>
        <div className="import-results__stat">
          <span>•</span>
          <strong>{results.created_cards}</strong> Verbkarten erstellt
        </div>
        {results.vocabulary_cards_created > 0 && (
          <div className="import-results__stat">
            <span>•</span>
            <strong>{results.vocabulary_cards_created}</strong> Vokabelkarten erstellt
          </div>
        )}
      </div>

      {results.errors && results.errors.length > 0 && (
        <div className="import-results__errors">
          <strong>Fehler:</strong>
          {results.errors.map((err, i) => (
            <div key={i}>• {err.error}</div>
          ))}
        </div>
      )}

      <div className="import-results__actions">
        <button
          className="staged-verbs__success-btn"
          type="button"
          onClick={onAddMore}
        >
          Weitere Verben
        </button>
        <button
          className="staged-verbs__success-btn staged-verbs__success-btn--primary"
          type="button"
          onClick={onGoToTraining}
        >
          Zum Training →
        </button>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

function StagedVerbsSection() {
  const navigate = useNavigate();
  const { reloadData } = useVocabulary();

  // Add verb state
  const [inputValue, setInputValue] = useState('');
  const [addState, setAddState] = useState('idle');
  const [addError, setAddError] = useState('');
  const [addedVerb, setAddedVerb] = useState(null);
  const [duplicateVerb, setDuplicateVerb] = useState(null);
  const [similarVerbs, setSimilarVerbs] = useState([]);

  // List state
  const [stagedVerbs, setStagedVerbs] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedVerbIds, setSelectedVerbIds] = useState(new Set());

  // Detail dialog
  const [detailVerb, setDetailVerb] = useState(null);

  // Import dialog
  const [importDialogVerbs, setImportDialogVerbs] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const inputRef = useRef(null);

  // ---- Load staged verbs ----
  const loadStagedVerbs = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      params.limit = 200;
      const result = await stagedVerbApi.listVerbs(params);
      setStagedVerbs(result.verbs || []);
    } catch (err) {
      console.error('Failed to load staged verbs:', err);
    } finally {
      setIsLoadingList(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadStagedVerbs();
  }, [loadStagedVerbs]);

  // ---- Add verb handler ----
  const handleAddVerb = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    setAddError('');
    setAddedVerb(null);
    setDuplicateVerb(null);
    setSimilarVerbs([]);

    setAddState('checking');
    try {
      const checkResult = await stagedVerbApi.checkVerb(trimmed);

      if (checkResult.exists && checkResult.existing_verb) {
        setDuplicateVerb(checkResult.existing_verb);
        setSimilarVerbs(checkResult.similar_verbs || []);
        setAddState('duplicate');
        return;
      }

      if (checkResult.similar_verbs && checkResult.similar_verbs.length > 0) {
        setSimilarVerbs(checkResult.similar_verbs);
      }
    } catch (err) {
      console.warn('Verb check failed, proceeding:', err);
    }

    setAddState('processing');
    try {
      const result = await stagedVerbApi.addVerb(trimmed);

      if (result.status === 'exists') {
        setDuplicateVerb(result.verb);
        setAddState('duplicate');
        return;
      }

      setAddedVerb(result.verb);
      setAddState('success');
      setInputValue('');
      loadStagedVerbs();
    } catch (err) {
      setAddError(err.message || 'Verb konnte nicht hinzugefügt werden');
      setAddState('error');
    }
  }, [inputValue, loadStagedVerbs]);

  const handleAddAnother = useCallback(() => {
    setAddState('idle');
    setAddedVerb(null);
    setAddError('');
    setDuplicateVerb(null);
    setSimilarVerbs([]);
    setImportResults(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleDismissDuplicate = useCallback(() => {
    setAddState('idle');
    setDuplicateVerb(null);
    setSimilarVerbs([]);
  }, []);

  // ---- Selection handlers ----
  const handleToggleSelect = useCallback((verbId) => {
    setSelectedVerbIds((prev) => {
      const next = new Set(prev);
      if (next.has(verbId)) {
        next.delete(verbId);
      } else {
        next.add(verbId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const readyVerbs = stagedVerbs.filter((v) => v.status === 'ready');
    if (selectedVerbIds.size === readyVerbs.length && readyVerbs.length > 0) {
      setSelectedVerbIds(new Set());
    } else {
      setSelectedVerbIds(new Set(readyVerbs.map((v) => v.id)));
    }
  }, [stagedVerbs, selectedVerbIds]);

  // ---- View detail ----
  const handleViewDetail = useCallback(async (verb) => {
    try {
      const detail = await stagedVerbApi.getVerb(verb.id);
      setDetailVerb(detail);
    } catch (err) {
      console.error('Failed to load verb detail:', err);
      setDetailVerb(verb);
    }
  }, []);

  const handleDetailUpdate = useCallback((updatedVerb) => {
    setDetailVerb(updatedVerb);
    setStagedVerbs((prev) =>
      prev.map((v) => (v.id === updatedVerb.id ? { ...v, ...updatedVerb } : v))
    );
  }, []);

  // ---- Delete handler ----
  const handleDeleteVerb = useCallback((verb) => {
    setDeleteTarget(verb);
    setDetailVerb(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;

    const wasImported = deleteTarget.status === 'imported';
    setIsDeleting(true);

    try {
      // 1. Delete from staged verbs API
      await stagedVerbApi.deleteVerb(deleteTarget.id);

      // 2. Update local staged verbs list
      setStagedVerbs((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      setSelectedVerbIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });

      // 3. If the verb was imported, its cards may have been affected on the backend
      //    Reload the main vocabulary context so cards/verbCards reflect the change
      if (wasImported) {
        console.log('[StagedVerbs] Deleted imported verb — reloading context data');
        await reloadData();
      }

      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete verb:', err);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, reloadData]);

  // ---- Retry handler ----
  const handleRetry = useCallback(async (verb) => {
    try {
      const result = await stagedVerbApi.reprocessVerb(verb.id);
      setStagedVerbs((prev) =>
        prev.map((v) => (v.id === verb.id ? { ...v, ...result } : v))
      );
    } catch (err) {
      console.error('Failed to reprocess verb:', err);
    }
  }, []);

  // ---- Import handlers ----
  const handleOpenImportDialog = useCallback((verbs, preselectedForms) => {
    const verbsToImport = verbs || stagedVerbs.filter((v) => selectedVerbIds.has(v.id));
    if (verbsToImport.length === 0) return;

    if (preselectedForms) {
      const updatedVerbs = verbsToImport.map((v) => ({
        ...v,
        selected_forms: preselectedForms,
      }));
      setImportDialogVerbs(updatedVerbs);
    } else {
      setImportDialogVerbs(verbsToImport);
    }
    setDetailVerb(null);
  }, [stagedVerbs, selectedVerbIds]);

  const handleImportConfirm = useCallback(async (verbs, selectedForms, addToVocabulary) => {
    setIsImporting(true);
    try {
      // 1. Call the import API
      const result = await stagedVerbApi.importVerbs({
        verb_ids: verbs.map((v) => v.id),
        selected_forms: selectedForms,
        add_to_vocabulary: addToVocabulary,
      });

      // 2. Close the import dialog immediately
      setImportDialogVerbs(null);
      setSelectedVerbIds(new Set());

      // 3. Reload the main vocabulary context so cards/verbCards are fresh
      await reloadData();

      // 4. Refresh staged verbs list
      await loadStagedVerbs();

      // 5. Now show results (context is already updated at this point)
      setImportResults(result);
    } catch (err) {
      console.error('Failed to import verbs:', err);
    } finally {
      setIsImporting(false);
    }
  }, [loadStagedVerbs, reloadData]);

  const handleGoToTraining = useCallback(() => {
    setImportResults(null);
    navigate('/');
  }, [navigate]);

  const handleDismissResults = useCallback(() => {
    setImportResults(null);
  }, []);

  // ---- Computed values ----
  const filteredVerbs = useMemo(() => {
    return stagedVerbs;
  }, [stagedVerbs]);

  const readyCount = useMemo(() => {
    return stagedVerbs.filter((v) => v.status === 'ready').length;
  }, [stagedVerbs]);

  const selectedReadyVerbs = useMemo(() => {
    return stagedVerbs.filter((v) => selectedVerbIds.has(v.id) && v.status === 'ready');
  }, [stagedVerbs, selectedVerbIds]);

  const statusCounts = useMemo(() => {
    const counts = { all: 0, ready: 0, imported: 0, error: 0, pending: 0 };
    stagedVerbs.forEach((v) => {
      counts.all++;
      if (counts[v.status] !== undefined) counts[v.status]++;
    });
    return counts;
  }, [stagedVerbs]);

  return (
    <div className="staged-verbs">
      {/* ---- Add Verb Section ---- */}
      <div className="staged-verbs__add-section">
        <h3 className="staged-verbs__add-title">
          <span role="img" aria-hidden="true">✏️</span>
          Eigenes Verb hinzufügen
        </h3>

        {addState === 'idle' || addState === 'checking' || addState === 'error' ? (
          <>
            <form className="staged-verbs__add-form" onSubmit={handleAddVerb}>
              <input
                ref={inputRef}
                className="staged-verbs__add-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Verb eingeben (z.B. mangio, dormire, ho parlato)"
                disabled={addState === 'checking'}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <button
                className="staged-verbs__add-btn"
                type="submit"
                disabled={!inputValue.trim() || addState === 'checking'}
              >
                {addState === 'checking' ? (
                  <>
                    <span className="staged-verbs__processing-spinner" />
                    Prüfe…
                  </>
                ) : (
                  'Hinzufügen'
                )}
              </button>
            </form>

            {addState === 'error' && addError && (
              <div className="staged-verbs__error">
                ❌ {addError}
              </div>
            )}

            {similarVerbs.length > 0 && addState !== 'error' && (
              <div className="staged-verbs__similar">
                <div className="staged-verbs__similar-title">Ähnliche Verben:</div>
                <div className="staged-verbs__similar-list">
                  {similarVerbs.map((sv) => (
                    <span key={sv.id} className="staged-verbs__similar-item">
                      {sv.italian_infinitive} ({sv.german_infinitive || '—'})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}

        {addState === 'processing' && (
          <div className="staged-verbs__processing">
            <span className="staged-verbs__processing-spinner" />
            Verb wird analysiert und Konjugationen werden generiert…
          </div>
        )}

        {addState === 'success' && addedVerb && (
          <div className="staged-verbs__success">
            <div className="staged-verbs__success-header">
              ✅ Verb erfolgreich hinzugefügt!
            </div>
            <div className="staged-verbs__success-detail">
              <strong>„{addedVerb.user_input}"</strong> → <strong>{addedVerb.italian_infinitive}</strong>
              {addedVerb.german_infinitive && (
                <> ({addedVerb.german_infinitive})</>
              )}
              {addedVerb.detected_form && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)', marginTop: '2px' }}>
                  Erkannte Form: {addedVerb.detected_form}
                </div>
              )}
            </div>
            <div className="staged-verbs__success-actions">
              <button
                className="staged-verbs__success-btn staged-verbs__success-btn--primary"
                type="button"
                onClick={() => handleViewDetail(addedVerb)}
              >
                Details anzeigen
              </button>
              <button
                className="staged-verbs__success-btn"
                type="button"
                onClick={handleAddAnother}
              >
                Weiteres Verb
              </button>
            </div>
          </div>
        )}

        {addState === 'duplicate' && duplicateVerb && (
          <div className="staged-verbs__duplicate">
            <div className="staged-verbs__duplicate-header">
              ⚠️ Verb existiert bereits
            </div>
            <div className="staged-verbs__duplicate-info">
              <strong>{duplicateVerb.italian_infinitive}</strong>
              {duplicateVerb.german_infinitive && ` (${duplicateVerb.german_infinitive})`}
              {' — '}
              <StatusBadge status={duplicateVerb.status} />
            </div>
            <div className="staged-verbs__duplicate-actions">
              <button
                className="staged-verbs__success-btn"
                type="button"
                onClick={() => {
                  handleViewDetail(duplicateVerb);
                  setAddState('idle');
                  setDuplicateVerb(null);
                }}
              >
                Details anzeigen
              </button>
              <button
                className="staged-verbs__success-btn"
                type="button"
                onClick={handleDismissDuplicate}
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Import Results ---- */}
      {importResults && (
        <ImportResults
          results={importResults}
          onGoToTraining={handleGoToTraining}
          onAddMore={handleAddAnother}
          onDismiss={handleDismissResults}
        />
      )}

      {/* ---- Staged Verbs List ---- */}
      <div className="staged-verbs__list-section">
        <div className="staged-verbs__list-header">
          <h3 className="staged-verbs__list-title">
            <span role="img" aria-hidden="true">📋</span>
            Bereitgestellte Verben ({stagedVerbs.length})
          </h3>

          <div className="staged-verbs__list-actions">
            {selectedReadyVerbs.length > 0 && (
              <button
                className="staged-verbs__import-selected-btn"
                type="button"
                onClick={() => handleOpenImportDialog(selectedReadyVerbs)}
              >
                📥 {selectedReadyVerbs.length} importieren
              </button>
            )}
          </div>
        </div>

        <div className="staged-verbs__filter-bar">
          {[
            { key: 'all', label: 'Alle' },
            { key: 'ready', label: 'Bereit' },
            { key: 'imported', label: 'Importiert' },
            { key: 'error', label: 'Fehler' },
            { key: 'pending', label: 'Ausstehend' },
          ].map((filter) => (
            <button
              key={filter.key}
              className={`staged-verbs__filter-btn ${statusFilter === filter.key ? 'staged-verbs__filter-btn--active' : ''}`}
              type="button"
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              {statusCounts[filter.key] > 0 && ` (${statusCounts[filter.key]})`}
            </button>
          ))}
        </div>

        {isLoadingList ? (
          <div className="staged-verbs__empty">
            <span className="staged-verbs__empty-icon">⏳</span>
            <p className="staged-verbs__empty-text">Laden…</p>
          </div>
        ) : filteredVerbs.length === 0 ? (
          <div className="staged-verbs__empty">
            <span className="staged-verbs__empty-icon" role="img" aria-hidden="true">📭</span>
            <p className="staged-verbs__empty-text">
              {statusFilter !== 'all'
                ? `Keine Verben mit Status „${VERB_STATUS_CONFIG[statusFilter]?.label || statusFilter}"`
                : 'Noch keine eigenen Verben hinzugefügt. Gib oben ein Verb ein, um zu starten!'
              }
            </p>
          </div>
        ) : (
          <table className="staged-verbs__table" role="table" aria-label="Bereitgestellte Verben">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="staged-verbs__table-checkbox"
                    checked={readyCount > 0 && selectedVerbIds.size === readyCount}
                    onChange={handleSelectAll}
                    disabled={readyCount === 0}
                    aria-label="Alle auswählen"
                  />
                </th>
                <th>Verb</th>
                <th>Deutsch</th>
                <th>Status</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filteredVerbs.map((verb) => {
                const isReady = verb.status === 'ready';
                const isError = verb.status === 'error';
                const isImported = verb.status === 'imported';
                return (
                  <tr key={verb.id}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        className="staged-verbs__table-checkbox"
                        checked={selectedVerbIds.has(verb.id)}
                        onChange={() => handleToggleSelect(verb.id)}
                        disabled={!isReady}
                      />
                    </td>
                    <td>
                      <strong style={{ color: 'var(--color-wood-medium)' }}>
                        {verb.italian_infinitive}
                      </strong>
                      {verb.user_input !== verb.italian_infinitive && (
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-light)' }}>
                          Eingabe: {verb.user_input}
                        </div>
                      )}
                    </td>
                    <td>{verb.german_infinitive || '—'}</td>
                    <td>
                      <StatusBadge status={verb.status} />
                    </td>
                    <td>
                      <div className="staged-verbs__table-actions">
                        {(isReady || isImported) && (
                          <button
                            className="staged-verbs__table-action-btn"
                            type="button"
                            onClick={() => handleViewDetail(verb)}
                            aria-label={`Details für ${verb.italian_infinitive}`}
                            title="Details"
                          >
                            👁️
                          </button>
                        )}
                        {isError && (
                          <button
                            className="staged-verbs__table-action-btn staged-verbs__table-action-btn--retry"
                            type="button"
                            onClick={() => handleRetry(verb)}
                            aria-label={`${verb.italian_infinitive} erneut verarbeiten`}
                            title="Erneut versuchen"
                          >
                            🔄
                          </button>
                        )}
                        <button
                          className="staged-verbs__table-action-btn staged-verbs__table-action-btn--delete"
                          type="button"
                          onClick={() => handleDeleteVerb(verb)}
                          aria-label={`${verb.italian_infinitive} löschen`}
                          title="Löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Detail Dialog ---- */}
      {detailVerb && (
        <VerbDetailDialog
          verb={detailVerb}
          onClose={() => setDetailVerb(null)}
          onImport={handleOpenImportDialog}
          onDelete={handleDeleteVerb}
          onUpdate={handleDetailUpdate}
        />
      )}

      {/* ---- Import Dialog ---- */}
      {importDialogVerbs && (
        <ImportDialog
          verbs={importDialogVerbs}
          onClose={() => !isImporting && setImportDialogVerbs(null)}
          onConfirm={handleImportConfirm}
          isImporting={isImporting}
        />
      )}

      {/* ---- Delete Confirmation ---- */}
      {deleteTarget && (
        <div
          className="verb-detail-overlay"
          onClick={() => !isDeleting && setDeleteTarget(null)}
          role="alertdialog"
          aria-modal="true"
          aria-label="Verb löschen"
        >
          <div className="import-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="import-dialog__icon" role="img" aria-hidden="true">⚠️</div>
            <h3 className="import-dialog__title">Verb löschen?</h3>
            <p style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              lineHeight: 'var(--line-height-normal)',
            }}>
              Möchtest du <strong style={{ color: 'var(--color-error)' }}>
                {deleteTarget.italian_infinitive}
              </strong> wirklich löschen?
              {deleteTarget.status === 'imported' && (
                <span style={{ display: 'block', marginTop: 'var(--space-xs)', fontSize: 'var(--font-size-xs)', color: 'var(--color-accent)' }}>
                  ⚠️ Bereits importierte Karten werden ebenfalls entfernt.
                </span>
              )}
            </p>
            <div className="import-dialog__actions">
              <button
                className="import-dialog__btn import-dialog__btn--cancel"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                Abbrechen
              </button>
              <button
                className="import-dialog__btn"
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  backgroundColor: 'var(--color-error)',
                  color: 'white',
                }}
              >
                {isDeleting ? (
                  <>
                    <span className="staged-verbs__processing-spinner" />
                    Löschen…
                  </>
                ) : (
                  'Löschen'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StagedVerbsSection;